import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { useEffect } from 'react';
import { Button } from 'primereact/button';
import { formatDate } from '../../../utils/helper';
import ManagerDialogStore from '../stores/ManagerDialogStore';
import { ManagerStore } from '../';
import { MatenTable, MatenTableAction, MatenTableColumn } from '../../../shared';

const ManagerOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ManagerStore);
    }, [sdk]);

    // Define columns for managers
    const columns: MatenTableColumn[] = [
        {
            field: 'id',
            header: 'Id',
            sortable: true,
        },
        {
            field: 'firstname',
            header: 'Vorname',
            sortable: true,
        },
        {
            field: 'lastname',
            header: 'Nachname',
            sortable: true,
        },
        {
            field: 'email',
            header: 'E-Mail',
            sortable: true,
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created),
        },
        {
            field: 'updated',
            header: 'Aktualisiert',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.updated),
        },
    ];

    // Define actions for managers
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Objektleiter bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(ManagerDialogStore, { manager_id: rowData.id });
            },
            onTestId: rowData => `manager-button-edit-${rowData.id}`,
        },
        {
            icon: 'pi pi-trash',
            tooltip: 'Objektleiter löschen',
            tooltipPosition: 'left',
            onClick: async (rowData: any) => {
                const result = await sdk.getDialogDelete({
                    title: 'Manager löschen bestätigen',
                    message: `Um den Manager '${rowData.firstname} ${rowData.lastname}' zu löschen, geben Sie bitte das Wort löschen in das Feld unten ein.`,
                    searchWord: 'löschen',
                    onConfirm: () => sdk.currentStore.onDeleteManager(rowData),
                });
                if (result.confirmed) {
                    sdk.showSuccessToast('Objektleiter erfolgreich gelöscht');
                    await sdk.currentStore.fetchList();
                } else if (result.error) {
                    sdk.showErrorToast(`Fehler beim Löschen des Objektleiters: ${result.error}`);
                }
            },
            onTestId: rowData => `manager-button-delete-${rowData.id}`,
            className: 'p-button-danger',
        },
    ];

    // Additional header content
    const headerContent = (
        <Button
            label={'Neu'}
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(ManagerDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
            data-testid="manager-button-new"
        />
    );

    return (
        <MatenTable
            data={sdk.currentStore?.list}
            store={sdk.currentStore}
            columns={columns}
            actions={actions}
            search={{
                placeholder: 'Objektleiter durchsuchen',
            }}
            emptyMessage="Keine Objektleiter gefunden"
            entity={{ singular: 'Objektleiter', plural: 'Objektleiter' }}
            testId={'manager'}
            toolbarContent={headerContent}
            pagination={{ rows: 30 }}
        />
    );
});

export default ManagerOverview;
